import { createSlice } from "@reduxjs/toolkit";

// Single source of truth for periods
export const TIME_PERIODS = [
  {label: "Last 7 days", key: "last7Days"},
  {label: "Last 30 days", key: "last30Days"},
  {label: "Last 365 days", key: "last365Days"},
  {label: "Custom", key: "custom"}
];
export const DEFAULT_PERIOD = TIME_PERIODS[1].key; // Last 30 days

const initialState = {
  strategiesByPeriod: TIME_PERIODS.reduce((acc, period) => {
    acc[period?.key] = null;
    return acc;
  }, {}),
  currentPeriod: DEFAULT_PERIOD,
  loading: false,
  error: null,
  strategyDetail: {
    strategies: [],
    ownerStrategies: [],
    annualPerformanceData: null,
    loading: false,
    annualPerformanceLoading: false,
    period: DEFAULT_PERIOD,
  },
};

const strategyStoreSlice = createSlice({
  name: "strategyStore",
  initialState,
  reducers: {
    setStrategiesForPeriod: (state, action) => {
      const { period, strategies } = action.payload;
      state.strategiesByPeriod[period] = strategies;
      state.loading = false;
      state.error = null;
    },
    setStrategyStoreLoading: (state, action) => {
      state.loading = action.payload;
    },
    setStrategyStoreError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearStrategyStoreCache: (state) => {
      state.strategiesByPeriod = initialState.strategiesByPeriod;
    },
    setCurrentPeriod: (state, action) => {
      state.currentPeriod = action.payload;
    },
    switchPeriod: (state, action) => {
      const newPeriod = action.payload;
      state.currentPeriod = newPeriod;
      if (!state.strategiesByPeriod[newPeriod]) {
        state.loading = true;
      }
    },
    setDetailStrategies: (state, action) => {
      state.strategyDetail.strategies = action.payload;
    },
    addComparisonStrategy: (state, action) => {
      state.strategyDetail.strategies.push(action.payload);
    },
    removeComparisonStrategy: (state, action) => {
      state.strategyDetail.strategies = state.strategyDetail.strategies.filter(
        (s, index) => index === 0 || s.idStrategy !== action.payload
      );
    },
    setDetailAnnualPerformance: (state, action) => {
      state.strategyDetail.annualPerformanceData = action.payload;
    },
    setAnnualPerformanceLoading: (state, action) => {
      state.strategyDetail.annualPerformanceLoading = action.payload;
    },
    setDetailLoading: (state, action) => {
      state.strategyDetail.loading = action.payload;
    },
    setDetailPeriod: (state, action) => {
      state.strategyDetail.period = action.payload;
    },
    setOwnerStrategies: (state, action) => {
      state.strategyDetail.ownerStrategies = action.payload;
    },
    clearStrategyDetail: (state) => {
      state.strategyDetail = { strategies: [], ownerStrategies: [], annualPerformanceData: null, loading: false, annualPerformanceLoading: false, period: DEFAULT_PERIOD };
    },
  }
});

export const {
  setStrategiesForPeriod,
  setStrategyStoreLoading,
  setStrategyStoreError,
  clearStrategyStoreCache,
  setCurrentPeriod,
  switchPeriod,
  setDetailStrategies,
  addComparisonStrategy,
  removeComparisonStrategy,
  setDetailAnnualPerformance,
  setAnnualPerformanceLoading,
  setDetailLoading,
  setDetailPeriod,
  setOwnerStrategies,
  clearStrategyDetail,
} = strategyStoreSlice.actions;

// Selectors
export const selectStrategiesForPeriod = (period) => (state) =>
  state.strategyStore.strategiesByPeriod[period];

export const selectStrategyStoreLoading = (state) => state.strategyStore.loading;
export const selectStrategyStoreError = (state) => state.strategyStore.error;
export const selectCurrentPeriod = (state) => state.strategyStore.currentPeriod;

export const selectDetailPeriod = (state) => state.strategyStore.strategyDetail.period;
export const selectDetailStrategies = (state) => state.strategyStore.strategyDetail.strategies;
export const selectMainStrategy = (state) => state.strategyStore.strategyDetail.strategies[0];
export const selectCompareStrategies = (state) => state.strategyStore.strategyDetail.strategies.slice(1);
export const selectDetailAnnualPerformance = (state) => state.strategyStore.strategyDetail.annualPerformanceData;
export const selectDetailLoading = (state) => state.strategyStore.strategyDetail.loading;
export const selectAnnualPerformanceLoading = (state) => state.strategyStore.strategyDetail.annualPerformanceLoading;
export const selectOwnerStrategies = (state) => state.strategyStore.strategyDetail.ownerStrategies;

export default strategyStoreSlice.reducer;
